import * as CL from '@design-system/component-library';
import { useState } from 'react';
import { Link, generatePath, useNavigate, useRevalidator } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { paths } from '../../common/constants/pathVariables.js';
import { StatusColor } from '../../common/enums.js';
import { useSearchParams } from '../../common/hooks/useSearchParams.js';
import {
  addAdminMsg,
  cancelMsg,
  cancelledMsg,
  companyMsg,
  confirmedMsg,
  invitationExpiredMsg,
  inviteAdminModalMsg,
  inviteEmployeesMsg,
  sendingFailedMsg,
  sendingMsg,
  statusMsg,
  t,
  userMsg,
  waitingForConfirmationMsg,
} from '../../common/i18n/index.js';
import { getCompanyName } from '../../common/utils/accountUtils.js';
import { sortArray } from '../../common/utils/arrayUtils.js';
import {
  formatTimeStampToHHmm,
  formatTimeStampToYYYYMMDDHHmm,
  formatTimestampToDDMMYYYY,
} from '../../common/utils/dateUtils.js';
import { highlightKeyword } from '../../common/utils/searchFieldUtils.js';
import { InvitationProfileEnum } from '../../generated/api/invitationProfileEnum.js';
import { InvitationStatus } from '../../generated/api/invitationStatus.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { Grid } from '../Grid/Grid.js';
import { InvitationActionType, getInvitationActionButtons } from '../InvitationActionButton/InvitationActionButton.js';
import { InviteEmployeesModal } from '../InviteEmployeesModal/InviteEmployeesModal.js';
import { Status } from '../Status/Status.js';
import { ListSearch, Table } from '../Table/index.js';
import type { CLTableColumn } from '../../common/types/componentLibrary.js';
import type { Invitation } from '../../generated/api/invitation.js';
import type { DefaultListSearchParams } from '../Table/index.js';

import './InvitationList.scss';

enum InvitationListFields {
  USER = 'user',
  SENT = 'sent',
  SENDER = 'sender',
  STATUS = 'status',
  COMPANY = 'company',
  ROLE = 'role',
  ACTION = 'action',
}

export interface InvitationListProps {
  invitations: Invitation[];
}

interface EnrichedInvitation extends Invitation {
  statusColor: StatusColor;
  statusText: string;
  expiresText?: string;
  sentDate?: string;
  sentTime?: string;
  roleText: string;
  actionTypes: InvitationActionType[];
}

const enrichInvitations = (invitations: Invitation[]): EnrichedInvitation[] => {
  return invitations.map(invitation => {
    const sentDate = invitation.sentAt ? formatTimestampToDDMMYYYY(invitation.sentAt)! : undefined;
    const sentTime = invitation.sentAt ? formatTimeStampToHHmm(invitation.sentAt) : undefined;
    const roleText = invitation.profile === InvitationProfileEnum.CUSTOMER_ADMIN ? t.E112('Admin') : t.R7FZ('Employee');

    switch (invitation.status) {
      case InvitationStatus.Created:
        return {
          ...invitation,
          statusColor: StatusColor.YELLOW,
          statusText: t.N1WI(sendingMsg),
          sentDate,
          sentTime,
          roleText,
          actionTypes: [InvitationActionType.CANCEL],
        };
      case InvitationStatus.Sent:
        return {
          ...invitation,
          statusColor: StatusColor.YELLOW,
          statusText: t.HSBQ(waitingForConfirmationMsg),
          sentDate,
          sentTime,
          roleText,
          expiresText: invitation.expiresAt
            ? t.AHWD('Active until {}', formatTimeStampToYYYYMMDDHHmm(invitation.expiresAt))
            : undefined,
          actionTypes: [InvitationActionType.CANCEL],
        };
      case InvitationStatus.Used:
        return {
          ...invitation,
          statusColor: StatusColor.GREEN,
          statusText: t.KQOD(confirmedMsg),
          sentDate,
          sentTime,
          roleText,
          actionTypes: [],
        };
      case InvitationStatus.Expired:
        return {
          ...invitation,
          statusColor: StatusColor.RED,
          statusText: t.M0U2(invitationExpiredMsg),
          sentDate,
          sentTime,
          roleText,
          actionTypes: [InvitationActionType.RESEND, InvitationActionType.REMOVE],
        };
      case InvitationStatus.Error:
        return {
          ...invitation,
          statusColor: StatusColor.RED,
          statusText: t.VM5M(sendingFailedMsg),
          sentDate,
          sentTime,
          roleText,
          actionTypes: [InvitationActionType.RESEND, InvitationActionType.REMOVE],
        };
      case InvitationStatus.Cancelled:
      default:
        return {
          ...invitation,
          statusColor: StatusColor.GREY,
          statusText: t.PEKP(cancelledMsg),
          sentDate,
          sentTime,
          roleText,
          actionTypes: [],
        };
    }
  });
};

const StatusColumn = ({ invitation }: { invitation: EnrichedInvitation }) => (
  <Status
    key={0}
    color={invitation.statusColor}
    textAfterBadge={
      <>
        <div>{invitation.statusText}</div>
        {invitation.expiresText && (
          <div className={`of-invitation-list disclaimer ${dsClass.TEXT_XS}`}>{invitation.expiresText}</div>
        )}
      </>
    }
  />
);

const InviteAdminModal = ({ onClose }: { onClose: () => void }) => {
  const navigate = useNavigate();
  return (
    <CL.Modal
      autoOpen
      heading={t.O4DB(addAdminMsg)}
      content={t.P44I(inviteAdminModalMsg)}
      buttons={[
        <CL.Button key="cancel" color="tertiary" onClick={onClose}>
          {t.B2V1(cancelMsg)}
        </CL.Button>,
        <CL.Button key="goToUsers" color="primary" onClick={() => navigate(paths.COMPANY_INFO_CONTACTS)}>
          {t.GIWH('Manage users')}
        </CL.Button>,
      ]}
      onModalClose={onClose}
    />
  );
};

const filterInvitations = (invitations: Invitation[], searchQuery = ''): Invitation[] =>
  invitations.filter(i => (i.contactName ?? i.email).toLowerCase().includes(searchQuery.trim().toLowerCase()));

const sortList = (invitations: EnrichedInvitation[], sort?: string, order?: string) => {
  return sort && order ? sortArray(invitations, sort, order) : sortArray(invitations, 'sentAt', 'desc');
};

const sortInvitations = (invitations: EnrichedInvitation[], sort?: string, order?: string) => {
  const itemList = sortList(invitations, sort, order);
  if (sort === 'status') {
    return order === 'asc'
      ? itemList?.sort((a, b) => (a.statusText < b.statusText ? -1 : 1))
      : itemList?.sort((a, b) => (a.statusText > b.statusText ? -1 : 1));
  }
  return itemList;
};

const UserField = ({ invitation }: { invitation: EnrichedInvitation }) => {
  const { search } = useSearchParams<DefaultListSearchParams>();
  return invitation.contactName ? (
    <Link
      to={`${generatePath(paths.COMPANY_INFO_CONTACT, { contactMasterId: invitation.contactMasterId })}?mdmId=${invitation.mdmId}`}
    >
      {highlightKeyword(invitation.contactName, search)}
    </Link>
  ) : (
    <>{highlightKeyword(invitation.email, search)}</>
  );
};

export const InvitationList = ({ invitations }: InvitationListProps) => {
  const { revalidate } = useRevalidator();
  const { authenticatedUser } = useAuth();
  const [showInviteAdminModal, setShowInviteAdminModal] = useState(false);
  const [showInviteEmployeesModal, setShowInviteEmployeesModal] = useState(false);
  const searchParams = useSearchParams<DefaultListSearchParams>();
  const { sort, order, search } = searchParams;

  const filteredInvitations = filterInvitations(invitations ?? [], search);
  const enrichedInvitations = enrichInvitations(filteredInvitations);
  const invitationsToDisplay = sortInvitations(enrichedInvitations, sort, order);

  const columns: CLTableColumn[] = [
    {
      key: InvitationListFields.USER,
      label: t.LUZM(userMsg),
      sortable: true,
    },
    {
      key: InvitationListFields.SENT,
      label: t.E9I4('Sent'),
      sortable: true,
    },
    {
      key: InvitationListFields.SENDER,
      label: t.RVSW('Sender'),
      sortable: true,
    },
    {
      key: InvitationListFields.STATUS,
      label: t.ASQT(statusMsg),
      sortable: true,
    },
    {
      key: InvitationListFields.COMPANY,
      label: t.KJTS(companyMsg),
      sortable: true,
    },
    {
      key: InvitationListFields.ROLE,
      label: t.H0UY('Role'),
      sortable: true,
    },
    {
      key: InvitationListFields.ACTION,
      ariaLabel: t.UAMB('Actions'),
      sortable: false,
    },
  ];

  const rows = invitationsToDisplay?.map(invitation => ({
    [InvitationListFields.USER]: <UserField invitation={invitation} />,
    [InvitationListFields.SENT]: (
      <>
        <div>{invitation.sentDate}</div>
        <div className={`of-invitation-list disclaimer ${dsClass.TEXT_XS}`}>{invitation.sentTime}</div>
      </>
    ),
    [InvitationListFields.SENDER]: <>{invitation.sender}</>,
    [InvitationListFields.STATUS]: <StatusColumn invitation={invitation} />,
    [InvitationListFields.COMPANY]: <>{getCompanyName(authenticatedUser, invitation.mdmId)}</>,
    [InvitationListFields.ROLE]: <>{invitation.roleText}</>,
    [InvitationListFields.ACTION]: <>{getInvitationActionButtons(invitation.invitationId, invitation.actionTypes)}</>,
  }));

  return (
    <>
      {showInviteAdminModal && <InviteAdminModal onClose={() => setShowInviteAdminModal(false)} />}
      {showInviteEmployeesModal && (
        <InviteEmployeesModal
          onClose={() => setShowInviteEmployeesModal(false)}
          onSubmitted={() => {
            setShowInviteEmployeesModal(false);
            revalidate();
          }}
        />
      )}
      <Grid className="of-invitation-list">
        <div className={dsClass.MARGIN_VERTICAL_4}>
          {t.XW5R(
            'You can track sent invitations to admins and employees. An invitation can be cancelled before it is activated. An expired invitation can be resent if needed. You can add a new admin on the'
          )}{' '}
          <Link to={paths.COMPANY_INFO_CONTACTS}>{t.FBQT('Users page')}.</Link>
        </div>
        <div className="of-invitation-buttons">
          <CL.Button color="secondary" onClick={() => setShowInviteAdminModal(true)}>
            {t.O4DB(addAdminMsg)}
          </CL.Button>
          <CL.Button color="secondary" onClick={() => setShowInviteEmployeesModal(true)}>
            {t.RLFR(inviteEmployeesMsg)}
          </CL.Button>
        </div>
        <ListSearch />
        <Table tableType="bordered" columns={columns} noItemsText={t.QYQX('No invites')} rows={rows || []} />
      </Grid>
    </>
  );
};
